import { useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'

import { RoutingPaths } from 'app/routes/routes'

import { DataForSearchTour } from 'entities/tour/models/search-tour/search-tour'

import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { flexibleDateRange } from 'shared/constants/tours'
import ErrorImage from 'shared/images/error.png'
import { dateFormat } from 'shared/lib/dateFormat'
import { IErrorForm, getFieldsError } from 'shared/lib/validate'
import { AllIcons } from 'shared/ui/app-icon/types'
import { Button } from 'shared/ui/button'
import { ButtonIconTextWithLabel } from 'shared/ui/button-icon-text-with-label'
import { DepArrCountryBlock } from 'shared/ui/dep-arr-country-block'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { Paper } from 'shared/ui/paper'
import { PopupContent } from 'shared/ui/poup-content'

import { useTourLimits } from '../../hooks/useTourLimits'

import { checkFormFindTour } from './lib/checkFormFindTour'
import s from './styles.module.scss'

interface Props {
  dataForSearchTour?: DataForSearchTour
  onSearch?: () => void
}

export const SearchFormTours = ({ dataForSearchTour, onSearch }: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { openDialog, closeDialog } = useDialogBottomSheet()
  const { checkHasLimitedTour } = useTourLimits()

  const [errors, setErrors] = useState<IErrorForm | undefined>(undefined)

  const handleValidSearch =
    onSearch ?? (() => navigate(RoutingPaths.tours.searchResults.path()))

  const onGoCalendar = () => {
    navigate(RoutingPaths.tours.calendar.path())
  }

  const onSelectNights = () => {
    navigate(RoutingPaths.tours.selectNights.path())
  }

  const onShowWarning = () => {
    openDialog({
      isDialog: false,
      child: (
        <PopupContent
          button={'Ok'}
          title={t('ops')}
          image={ErrorImage}
          description={t('tours_block.please_select_tour')}
          onClick={closeDialog}
        />
      ),
    })
  }

  const onSearchTour = async () => {
    try {
      await checkFormFindTour.validate(dataForSearchTour, {
        abortEarly: false,
      })

      handleValidSearch()

      setErrors(undefined)
    } catch (error) {
      const resultValidate = getFieldsError(error)

      setErrors(resultValidate)
    }
  }

  const onSearchDirection = (direction: string) => {
    if (direction === 'from') {
      navigate(RoutingPaths.tours.searchDeparture.path())
    } else if (direction === 'to') {
      if (!dataForSearchTour?.departure?.id) {
        onShowWarning()

        return
      }

      navigate(RoutingPaths.tours.searchDirection.path())
    }
  }

  let dateFlight = dateFormat(
    String(dataForSearchTour?.flight?.startDay || ''),
    DATE_FORMAT_LIST.DATE_MONTH_SHORT,
  )

  let directionNames = useMemo(
    () => dataForSearchTour?.resorts?.map((item) => item.name).join(', '),
    [dataForSearchTour?.resorts],
  )

  const countPassenger =
    (dataForSearchTour?.passengers?.adults || 0) +
    (dataForSearchTour?.passengers?.children || 0)
  const directionCountry =
    dataForSearchTour?.resorts?.[0]?.state_name ||
    dataForSearchTour?.country?.name
  let direction = ''

  if (directionCountry) {
    direction = directionNames
      ? `${directionCountry}, ${directionNames}`
      : directionCountry
  }

  const departure = dataForSearchTour?.departure

  let nightsInfo = String(dataForSearchTour?.duration || '')

  if (Boolean(dateFlight?.length) && dataForSearchTour?.is_flexible_date) {
    dateFlight += `, ±${flexibleDateRange}`
  }

  if (dataForSearchTour?.is_flexible_nights) {
    nightsInfo += `, ±${flexibleDateRange}`
  }

  return (
    <Paper
      padding={[16]}
      radius={28}
    >
      {/* <div className={s.searchHead}>
        <h3 className={clsx('extra-bold-24', s.searchTitle)}>
          {t('tours_block.time_to_rest')}
        </h3>
        <p className={clsx('regular-14', s.searchDescription)}>
          {t('tours_block.we_find_perfect_tour_for_you')}
        </p>
      </div> */}

      <div className={s.inputContainer}>
        <DepArrCountryBlock
          type="lightGrey"
          to={{ name: direction }}
          from={{ name: departure?.name || '' }}
          error={!!errors?.['resorts'] || !!errors?.['resorts']}
          onClick={onSearchDirection}
        />

        <div className={s.inputLine}>
          <div className={s.inputLineItem}>
            <ButtonIconTextWithLabel
              isBlackIcon
              value={dateFlight}
              placeholder={t('when')}
              rightIcon={AllIcons.CALENDAR_NEW}
              error={Boolean(errors?.['flight.startDay'])}
              onClick={onGoCalendar}
            />
          </div>
          <div className={s.inputLineItem}>
            <ButtonIconTextWithLabel
              isBlackIcon
              value={nightsInfo}
              rightIcon={AllIcons.MOON_ICON_INPUT}
              error={Boolean(errors?.['duration.id'])}
              placeholder={t('tours_block.nights_count')}
              onClick={onSelectNights}
            />
          </div>
        </div>

        <ButtonIconTextWithLabel
          isBlackIcon
          rightIcon={AllIcons.PEOPLE_NEW_INPUT}
          placeholder={t('tours_block.who_going_without_question')}
          value={
            countPassenger
              ? t('tours_block.count_passengers', { count: countPassenger })
              : ''
          }
          onClick={() => navigate(RoutingPaths.tours.selectPeople.path())}
        />
      </div>

      <div className={s.btnContainer}>
        <Button
          className={s.button}
          onClick={() => checkHasLimitedTour(onSearchTour)}
          borderRadius={'md'}
        >
          <span className={clsx(s.buttonText, 'semi-bold-17')}>
            {t('tours_block.find_tour')}
          </span>
        </Button>
      </div>
    </Paper>
  )
}
